import React, { useState } from 'react';
import { Download, Calendar, Filter, CheckCircle2, AlertTriangle } from 'lucide-react';
import { dashboardData, pumpConfigurations } from '../mockData';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('daily');
  const [date, setDate] = useState('2026-06-03');

  // Mock reconciliation data based on pumps
  const reconciliationData = pumpConfigurations.flatMap(pump => 
    pump.nozzles.map(nozzle => {
      const opening = 100000 + (pump.pumpId * 5000) + (nozzle.id * 1000);
      const actualClosing = opening + 2500; // Mock actual physical closing next morning
      const employeeReportedLiters = 2500; // Mock sum of all shifts
      
      // Let's create one mismatch for demonstration (Pump 3, Nozzle 2)
      const isMismatch = pump.pumpId === 3 && nozzle.id === 2;
      const reportedLiters = isMismatch ? 2450 : employeeReportedLiters; 
      
      const actualLiters = actualClosing - opening;
      const diff = actualLiters - reportedLiters;

      return {
        pumpId: pump.pumpId,
        nozzleId: nozzle.id,
        type: nozzle.type,
        opening,
        closing: actualClosing,
        actualLiters,
        reportedLiters,
        diff
      };
    })
  );

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports & Reconciliation</h1>
          <p className="text-slate-500 text-sm mt-1">Generate analysis and reconcile physical readings with employee accounting.</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="bg-white px-4 py-2 rounded-xl border border-slate-200 flex items-center gap-2 shadow-sm">
            <Calendar className="w-4 h-4 text-slate-400" />
            <input 
              type="date" 
              className="outline-none text-sm font-medium text-slate-700 bg-transparent"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <button className="btn-primary flex items-center gap-2">
            <Download className="w-4 h-4" /> Export PDF
          </button>
        </div>
      </div>

      <div className="glass-panel overflow-hidden">
        <div className="flex border-b border-slate-100 px-2 pt-2 bg-white/50 overflow-x-auto">
          {[
            { id: 'daily', label: 'Daily Financial Report' },
            { id: 'reconciliation', label: 'Nozzle Reconciliation' },
            { id: 'employee', label: 'Employee Performance' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === tab.id 
                  ? 'border-primary-500 text-primary-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          
          {/* DAILY FINANCIAL REPORT TAB */}
          {activeTab === 'daily' && (
            <div className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-slate-50 p-5 rounded-xl border border-slate-200">
                  <p className="text-sm font-medium text-slate-500 mb-1">Total Sales Amount</p>
                  <p className="text-2xl font-bold text-slate-900">₹{dashboardData.summary.totalSalesAmount.toLocaleString()}</p>
                </div>
                <div className="bg-slate-50 p-5 rounded-xl border border-slate-200">
                  <p className="text-sm font-medium text-slate-500 mb-1">Total Collections</p>
                  <p className="text-2xl font-bold text-slate-900">₹{dashboardData.summary.totalCollections.toLocaleString()}</p>
                </div>
                <div className="bg-slate-50 p-5 rounded-xl border border-slate-200">
                  <p className="text-sm font-medium text-slate-500 mb-1">Net Difference</p>
                  <p className={`text-2xl font-bold ${dashboardData.summary.difference < 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                    {dashboardData.summary.difference < 0 ? '-' : '+'}₹{Math.abs(dashboardData.summary.difference).toLocaleString()}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="font-bold text-slate-800 mb-4 text-lg">Payment Collection Breakdown</h3>
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 text-slate-600 text-sm">
                        <th className="p-4 font-semibold border-b border-slate-200">Payment Mode</th>
                        <th className="p-4 font-semibold text-right border-b border-slate-200">Amount Collected</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm bg-white">
                      {dashboardData.collectionsByMode.map(mode => (
                        <tr key={mode.name} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                          <td className="p-4 text-slate-700 font-medium">{mode.name}</td>
                          <td className="p-4 text-right font-bold text-slate-900">₹{mode.value.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* NOZZLE RECONCILIATION TAB */}
          {activeTab === 'reconciliation' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3 text-sm text-blue-800">
                <AlertTriangle className="w-5 h-5 text-blue-600 shrink-0" />
                <p>
                  This report compares the physical fuel pumped (Based on Morning Baseline Readings) against the sum of all liters reported by employees in their Daily Accounting. Any mismatch indicates unaccounted fuel or recording errors.
                </p>
              </div>

              <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
                <table className="w-full text-left border-collapse bg-white">
                  <thead>
                    <tr className="bg-slate-50 text-slate-600 text-sm">
                      <th className="p-4 font-semibold border-b border-slate-200">Pump / Nozzle</th>
                      <th className="p-4 font-semibold border-b border-slate-200">Start of Day Reading</th>
                      <th className="p-4 font-semibold border-b border-slate-200">Next Day Reading</th>
                      <th className="p-4 font-semibold border-b border-slate-200">Actual Liters Pumped</th>
                      <th className="p-4 font-semibold border-b border-slate-200">Employee Reported Liters</th>
                      <th className="p-4 font-semibold text-right border-b border-slate-200">Mismatch</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {reconciliationData.map((data, idx) => (
                      <tr key={idx} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                        <td className="p-4">
                          <div className="font-semibold text-slate-900">Pump {data.pumpId} - N{data.nozzleId}</div>
                          <div className="text-xs text-slate-500 mt-0.5">{data.type}</div>
                        </td>
                        <td className="p-4 text-slate-600 font-mono">{data.opening}</td>
                        <td className="p-4 text-slate-600 font-mono">{data.closing}</td>
                        <td className="p-4 font-semibold text-slate-700">{data.actualLiters.toFixed(2)} L</td>
                        <td className="p-4 font-semibold text-slate-700">{data.reportedLiters.toFixed(2)} L</td>
                        <td className="p-4 text-right">
                          {data.diff === 0 ? (
                            <span className="inline-flex items-center gap-1 text-emerald-600 font-bold bg-emerald-50 px-2.5 py-1 rounded-full text-xs">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Matched
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-600 font-bold bg-red-50 px-2.5 py-1 rounded-full text-xs">
                              <AlertTriangle className="w-3.5 h-3.5" /> {data.diff > 0 ? '-' : '+'}{Math.abs(data.diff).toFixed(2)} L Missing
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* EMPLOYEE PERFORMANCE TAB */}
          {activeTab === 'employee' && (
            <div className="text-center py-16 text-slate-500">
              <p>Employee performance report details will appear here.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
